import { Box, FormControl, FormLabel, Stack, Typography } from '@mui/joy';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { TextLine } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { formatDate } from 'core/utils';
import { CostValidityEntry } from 'quotes/enums';
import { COST_VALID_SINCE_FIELD_NAME, COST_VALID_UNTIL_FIELD_NAME } from 'quotes/utils';
import { Direction, Level } from 'theme/enums';

import { useCostFormFieldsContext } from './state';

const ReadOnlyDate = ({ label, date }: { label: string; date: string | null }) => (
  <Stack direction={Direction.ROW} sx={{ gap: 0.5, alignItems: 'baseline' }}>
    <TextLine text={`${label}:`} sx={{ flexShrink: 0 }} />
    {/* As the card's Cost Source value. */}
    <Box sx={{ minWidth: 0 }}>
      <TextLine text={formatDate(date)} bold textEllipsis />
    </Box>
  </Stack>
);

// One Validity Dates group in the cost card, under the vendor quote number.
export const CostValidityFields = () => {
  const { t } = useTranslation();

  const { watch } = useFormContext();
  const { control, errors, isExternalDisabled, oneTimeSpa, setCostValidityHeight } =
    useCostFormFieldsContext();

  const validSinceValue = watch(COST_VALID_SINCE_FIELD_NAME);

  const blockRef = useRef<HTMLDivElement>(null);
  const isShown = !!oneTimeSpa?.isShown;
  const entry = oneTimeSpa?.entry ?? CostValidityEntry.NONE;

  // The Replacement Cost card leaves a gap of this height, so both cards' Calculated Standard Cost
  // rows stay level.
  useLayoutEffect(() => {
    const block = blockRef.current;

    if (!block) {
      setCostValidityHeight(0);

      return undefined;
    }

    const reportHeight = () => setCostValidityHeight(block.offsetHeight);
    const observer = new ResizeObserver(reportHeight);

    reportHeight();
    observer.observe(block);

    return () => {
      observer.disconnect();
      setCostValidityHeight(0);
    };
  }, [entry, isShown, setCostValidityHeight]);

  if (!isShown) {
    return null;
  }

  if (CostValidityEntry.NONE === entry) {
    return (
      <Box ref={blockRef}>
        <FormLabel>{t('labelValidityDates')}</FormLabel>
        <ReadOnlyDate label={t('labelSince')} date={oneTimeSpa.resolvedEffectiveDate} />
        <ReadOnlyDate label={t('labelUntil')} date={oneTimeSpa.resolvedExpirationDate} />
        {/* Known only once saved: the BE resolves the cost's own dates. */}
        {oneTimeSpa.isOneTimeSpa &&
          !oneTimeSpa.isDatesPendingSave &&
          !oneTimeSpa.resolvedExpirationDate && (
            <Typography level={Level.BODY_XS} sx={{ color: 'text.tertiary' }}>
              {t('messageCostValidUntilMissing')}
            </Typography>
          )}
      </Box>
    );
  }

  const isTyped = CostValidityEntry.TYPED === entry;
  // A vendor quote cost's Valid since is its vendor quote's.
  const validSince = isTyped ? validSinceValue : oneTimeSpa.resolvedEffectiveDate;

  // The error shows as the input's border; its text reaches the form's validation notification.
  // The inputs have no label of their own: Valid Until's required marker shows on the group label
  // (Joy's asterisk, as on the field labels) and in its placeholder. Only the label is wrapped, so
  // the inputs keep their own FormControls.
  return (
    <Stack ref={blockRef} gap={0}>
      <FormControl required={oneTimeSpa.isExpirationDateRequired}>
        <FormLabel>{t('labelValidityDates')}</FormLabel>
      </FormControl>

      {isTyped ? (
        <FormField
          name={COST_VALID_SINCE_FIELD_NAME}
          type={InputType.DATE_INPUT}
          inputProps={{
            control,
            error: get(errors, COST_VALID_SINCE_FIELD_NAME),
            placeholder: t('labelValidSince'),
            disabled: isExternalDisabled,
          }}
        />
      ) : (
        <ReadOnlyDate label={t('labelSince')} date={validSince} />
      )}

      <FormField
        name={COST_VALID_UNTIL_FIELD_NAME}
        type={InputType.DATE_INPUT}
        inputProps={{
          control,
          error: get(errors, COST_VALID_UNTIL_FIELD_NAME),
          placeholder: t(
            oneTimeSpa.isExpirationDateRequired
              ? 'placeholderValidUntilRequired'
              : 'labelValidUntil'
          ),
          minDate: validSince ? dayjs(validSince) : undefined,
          required: oneTimeSpa.isExpirationDateRequired,
          disabled: isExternalDisabled,
        }}
      />
    </Stack>
  );
};
